import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  colors,
  css,
  fonts,
  glyphs,
  Units,
  useMediaQuery,
  useUnitsComponent,
} from "../../Styles";
import {
  Button,
  ButtonList,
  Overlay,
  FeedCurveViewer,
  NewSelect,
  ProgressCircle,
  NewModal,
  LineInfoDisplay,
  FieldMode,
} from "..";
import {
  ButtonTypes,
  ListStyle_Types,
  NewFieldTypes,
  NewFieldStyles,
  SelectFieldOption_Values,
} from "../../Definitions";
import { useAnimal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { useAppSelector } from "../../Store";
import { Graph_QL } from "../../Services";
import { useFeedCurves } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";

// TODO:
import Last10DaysViewer from "../curveViewer/animalGraph";
import { useSelectOptions } from "../_Components";
import { PanelMaintainAnimal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall";
import FeedCorrectionPopover from "../LocationFeeder/feedCorrectionPopover";

interface Feed_Props {
  selectedAnimal: number;
}
/**
 * Feed
 * @param selectedAnimal
 * @returns
 */
export default function Feed({ selectedAnimal }: Feed_Props) {
  const { t } = useTranslation();
  const getUnit = useUnitsComponent();
  const { feedcurveOptions } = useSelectOptions();
  const [showFeedViewModal, setFeedViewModal] = useState<boolean>();
  const [showFeedPlan, setShowFeedPlan] = useState<boolean>(false); // "Feed Plan"
  const [showLastDays, setShowLastDays] = useState<boolean>(false); // "Last 10 Days"
  const [editMode, setEditMode] = useState<boolean>(false);
  const dirtRef = useRef<PanelMaintainAnimal>({ animalId: selectedAnimal });

  useEffect(() => {
    dirtRef.current.animalId = selectedAnimal;
  }, [selectedAnimal]);

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { data: animalsState, maintainEntries: updateAnimals } =
    useAnimal(connection);

  // #region | Mediaqueries
  const mobile = useMediaQuery(
    `(max-width: 650px), (min-width: 1060px) and (max-width: 1390px), (min-width: 741px) and (max-width: 1000px)`
  );
  const mobileXs = useMediaQuery(
    `(max-width: 450px), (min-width: 1060px) and (max-width: 1150px)`
  );
  // #endregion

  // #region | Data
  // Get animal from selectedAnimal
  const thisAnimal = useMemo(() => {
    return animalsState && animalsState[domain]
      ? animalsState[domain].data.find(
          (animal) => animal.animal_id === selectedAnimal
        )
      : undefined;
  }, [animalsState, domain, selectedAnimal]);

  const consumption = useMemo(() => {
    const today = thisAnimal?.consumptions?.[0];
    const yesterday = thisAnimal?.consumptions?.[1];
  
    const consumptionToday = +((today?.total ?? 0) / 1000).toFixed(2);
    const consumptionProgressedToday = +(consumptionToday - (today?.progressed ?? 0) / 1000).toFixed(2);
  
    const consumptionYesterday = +((yesterday?.total ?? 0) / 1000).toFixed(2);
    const consumptionProgressedYesterday = +(consumptionYesterday - (yesterday?.progressed ?? 0) / 1000).toFixed(2);
  
    return {
      consumptionToday,
      consumptionProgressedToday,
      consumptionYesterday,
      consumptionProgressedYesterday
    };
  }, [thisAnimal?.consumptions]);

  // Animal data
  const feedCurve = (
    <>
      {thisAnimal?.feedCurve?.milestones && thisAnimal.feedCurve.components ? (
        <FeedCurveViewer
          data={thisAnimal.feedCurve}
          showComponentLabels
          showMilestoneLabels
        />
      ) : (
        <p
          style={{
            ...fonts.paragraph,
            ...fonts.fontSize4,
            color: colors.greyDarkPrimary,
          }}
        >
          {t("feed.feedCurve.noFeedCurve", {
            defaultValue: "No Feed Curve Found",
          })}
        </p>
      )}
    </>
  );

  const lastDays = (
    <div
      style={{
        marginTop: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "20px",
      }}
    >
      <Last10DaysViewer data={thisAnimal?.lastTenDay} />
    </div>
  );
  // #endregion

  // #region | Info box
  const info_feedPlan = (
    <LineInfoDisplay
      editMode={editMode}
      gridTemplateColumns={"7fr 25px 8fr"}
      value={thisAnimal?.feedCurve?.name}
      header={t("feed.feedCurve.feedCurve", { defaultValue: "Feed Curve" })}
      icon={"FeedPlan"}
      fieldMode={FieldMode.select}
      options={feedcurveOptions ?? []}
      onchange={(option: SelectFieldOption_Values | undefined) =>
        (dirtRef.current.feedCurveId = Number(option?.id))
      }
      defaultValue={
        thisAnimal && thisAnimal.feedCurve
          ? feedcurveOptions.find(
              ({ id }) => thisAnimal.feedCurve?.feedCurveId === id
            )
          : undefined
      }
    />
  );

  const info_corrections = (
    <LineInfoDisplay
      editMode={editMode}
      gridTemplateColumns={"7fr 25px 8fr"}
      value={`${thisAnimal?.correction ?? 0}%`}
      header={t("feed.feedCorrection.feedCorrection", {
        defaultValue: "Feed Corrections",
      })}
      icon={"FeedPlan"}
      fieldMode={FieldMode.feedCorrection}
      onchange={(e: string | number) =>
        (dirtRef.current.correction = Number(e))
      }
      defaultValue={thisAnimal?.correction ?? 0}
    />
  );
  // #endregion

  // #region | Feed graphs
  const feedGraphsBtns = (
    <ButtonList
      contents={[
        t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" }),
        t("feed.general.feedLastDays", { defaultValue: "last 10 days" }),
      ]}
      overrides={{
        baseButton: { ...css.button_FitContent },
      }}
      onClick={(i) => {
        if (i === 0) {
          setFeedViewModal(true);
          setShowFeedPlan(true);
        }
        if (i === 1) {
          setFeedViewModal(true);
          setShowLastDays(true);
        }
      }}
      listOverrides={{
        listStyle: ListStyle_Types.horizontal,
      }}
    />
  );

  const feedGraphsModals = (
    <NewModal
      modalState={!!showFeedViewModal}
      overrides={{
        backgroundColor: colors.greyLightTertiary,
      }}
      predefinedButtons={{
        onClose: () => {
          setFeedViewModal(false);
          setShowFeedPlan(false);
          setShowLastDays(false);
        },
      }}
      header={
        showFeedPlan
          ? t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" })
          : t("feed.general.feedLastDays", { defaultValue: "last 10 days" })
      }
      subHeader={`${t("animal.animalId", { defaultValue: "animal no." })} ${
        thisAnimal?.animal_id
      }`}
      mainContent={
        <div
          style={{
            padding: "0px 20px 20px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: showFeedPlan ? "20px" : "0px",
          }}
        >
          <div style={{ width: "fit-content" }}>
            {showFeedPlan ? <>{feedCurve}</> : <>{lastDays}</>}
          </div>
          <ButtonList
            contents={[
              t("feed.feedCurve.feedCurve", { defaultValue: "feed curve" }),
              t("feed.general.feedLastDays", { defaultValue: "last 10 days" }),
            ]}
            overrides={{
              baseButton: { ...css.button_FitContent },
            }}
            onClick={(i) => {
              if (i === 0) {
                setShowLastDays(false);
                setShowFeedPlan(true);
              }
              if (i === 1) {
                setShowFeedPlan(false);
                setShowLastDays(true);
              }
            }}
            listOverrides={{
              listStyle: ListStyle_Types.horizontal,
            }}
            hasActiveLoad
          />
        </div>
      }
    />
  );
  // #endregion

  return (
    <>
      {editMode ? <Overlay onClickAlert beforeUnload /> : <></>}
      <div style={{ position: "relative", height: "calc(100% - 50px)" }}>
        <div
          style={{
            display: "flex",
            position: "relative",
            justifyContent: "end",
            padding: "0px 20px",
            zIndex: editMode ? "9999" : "0",
          }}
        >
          {editMode ? (
            <ButtonList
              contents={[
                t("buttons.save", { defaultValue: "save" }),
                t("buttons.cancel", { defaultValue: "cancel" }),
                t("buttons.edit", { defaultValue: "edit" }),
              ]}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[
                glyphs.Check({}),
                glyphs.Close({}),
                glyphs.Edit({}),
              ]}
              onClick={(i) => {
                if (i === 0) {
                  setEditMode(false);
                  if (updateAnimals) {
                    updateAnimals({ data: [dirtRef.current] });
                  }
                  dirtRef.current = { animalId: selectedAnimal };
                }
                if (i === 1) {
                  setEditMode(false);
                  dirtRef.current = { animalId: selectedAnimal };
                }
              }}
              disabled={[false, false, true]}
              overrides={(index) => ({
                baseButton: {
                  backgroundColor:
                    index === 2 ? colors.greyPrimary : colors.greySecondary,
                },
                hoverStyles: { backgroundColor: colors.greyPrimary },
              })}
            />
          ) : (
            <Button
              content={t("buttons.edit", { defaultValue: "edit" })}
              buttonType={ButtonTypes.tableButton}
              startEnhancer={glyphs.Edit({})}
              onClick={() => setEditMode(true)}
            />
          )}
        </div>
        <div
          style={{
            backgroundColor: colors.greyLightTertiary,
            border: "5px solid " + colors.greyPrimary,
            borderRadius: "10px",
            display: "grid",
            gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
            gridTemplateRows: mobile ? "1fr 1fr" : "1fr",
            height: "100%",
            boxSizing: "border-box",
            overflowY: mobile ? "scroll" : "hidden",
            scrollbarColor: colors.greySecondary + colors.greyPrimary,
            scrollbarWidth: "thin",
          }}
        >
          <div
            style={{
              gridArea: "1/1/2/2",
              padding: "10px",
              display: "flex",
              alignItems: "start",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                ...css.modalTop,
                width: "fit-content",
                alignItems: "end",
                gap: mobileXs ? "40px" : "60px",
              }}
            >
              {/* Fed Today */}
              <ProgressCircle
                animation
                header={t("feed.general.remainingToday", {
                  defaultValue: "remaining today",
                })}
                total={consumption.consumptionToday}
                progressed={consumption.consumptionProgressedToday}
                unit={getUnit(Units.kG_grey)}
              />
              {/* Fed During Period */}
              <ProgressCircle
                animation
                // header={t("feed.general.fedLastPeriod", {defaultValue: "Fed Last Period"})}
                header={t("feed.general.remainingYesterday", {
                  defaultValue: "remaining yesterday",
                })}
                total={consumption.consumptionYesterday}
                progressed={consumption.consumptionProgressedYesterday}
                unit={getUnit(Units.kG_grey)}
              />
            </div>
          </div>
          <div
            style={{
              gridArea: mobile ? "2/1/3/2" : "1/2/2/3",
              padding: "10px",
              display: "flex",
              gap: "20px",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: colors.greyPrimary,
            }}
          >
            <div
              style={{
                zIndex: editMode ? "9999" : "0",
                width: "270px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {info_feedPlan}
              {info_corrections}
            </div>
            <div style={{ width: "80%", margin: "0 auto" }}>
              <div
                style={{
                  height: "1px",
                  backgroundColor: colors.greySecondary,
                  marginBottom: "20px",
                }}
              />
              <div style={{ display: "flex", justifyContent: "center" }}>
                {feedGraphsBtns}
              </div>
              {feedGraphsModals}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
